import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { Canvas, FabricImage, PencilBrush, Textbox } from "fabric";
import { PDFDocument } from "pdf-lib";
import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { FaCheck, FaEraser } from "react-icons/fa";
import { LuLoader2 } from "react-icons/lu";
import { Document, Page, pdfjs } from "react-pdf";

import { useLocation } from "react-router-dom";

import PdfToolbar from "./components/pdfToolbar";
import TextToolbar from "./components/textToolbar";
import ThumbnailSidebar from "./components/thumbnailSidebar";
import ZoomDropdown from "./components/zoomdropdown";
import Api from "../../config";
import { selectRole } from "../../states/slices/authReducer";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
  getSelectedCaseData,
  setSelectedCase,
} from "../../states/slices/partyInfo.slice";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.mjs`;

function PdfEditor() {
  const [numberPages, setNumberPages] = useState(0);
  const getcas = useAppSelector(getSelectedCaseData);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startedDrawing, setStartedDrawing] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1);
  const viewerRef = useRef<HTMLDivElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement | null>(null);
  const fabricRef = useRef<Canvas | null>(null);
  const { state } = useLocation();
  const [showdrawingbox, setShowdrawingbox] = useState(false);
  const canvaContainerRef = useRef<HTMLDivElement | null>(null);
  const dragHandleRef = useRef<HTMLDivElement>(null);
  const [downloadPdf, setDownloadPdf] = useState(false);
  const pdfOverlayContainerRef = useRef<HTMLDivElement | null>(null); // for Fabric overlay
  const pdfOverlayFabricRef = useRef<Canvas | null>(null); // for Fabric instance
  const [preloader, setPreloader] = useState(false);
  const [selectedColor, setSelectedColor] = useState("#ff0000");
  const role = useAppSelector(selectRole);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const pdfPageRef = useRef<HTMLDivElement | null>(null);
  const [pdfSize, setPdfSize] = useState({ width: 0, height: 0 });
  const [selectedStamp, setSelectedStamp] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [selectedFont, setSelectedFont] = useState("Helvetica");
  const [selectedSize, setSelectedSize] = useState("12 pt");
  const undoStack: object[] = [];
  const redoStack: object[] = [];

  const dispatch = useAppDispatch();
  const handleDocumentLoad = ({ numPages }: { numPages: number }) => {
    setNumberPages(numPages);
  };

  const handlePageOnloadSuccess = () => {
    if (!pdfPageRef.current) return;
    const { width, height } = pdfPageRef.current.getBoundingClientRect();
    if (width > 0 && height > 0) {
      setPdfSize({ width, height });
    }
  };

  useEffect(() => {
    if (state?.file_path) {
      setFileUrl(state?.file_path);
    }
  }, [state?.file_path]);

  useEffect(() => {
    // allows zoom of pdf onscroll with the control held
    const handleWheelZoom = (e: WheelEvent) => {
      if (e.ctrlKey) {
        e.preventDefault();
        if (e.deltaY < 0) {
          // scroll up zoom-in
          setScale((previous) => Math.min(previous + 0.1, 3));
        } else {
          // scroll down zoom-out
          setScale((previous) => Math.max(previous - 0.1, 0.5));
        }
      }
    };
    const container = viewerRef.current;
    if (container) {
      container.addEventListener("wheel", handleWheelZoom, { passive: false });
    }

    return () => {
      if (container) container.removeEventListener("wheel", handleWheelZoom);
    };
  }, []);

  useEffect(() => {
    // zoom in zoom out for touch screen
    let initialDistance: number | null = null;
    let initialScale: number = scale;

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        // if fingers on screen is two
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;

        initialDistance = Math.hypot(dx, dy);
        initialScale = scale;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && initialDistance !== null) {
        e.preventDefault();
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;

        const currentDistance = Math.hypot(dx, dy);
        const zoomFactor = currentDistance / initialDistance;
        const newScale = Math.min(Math.max(initialScale * zoomFactor, 0.5), 3);
        setScale(newScale);
      }
    };

    const container = viewerRef.current;
    if (container) {
      container.addEventListener("touchstart", handleTouchStart, {
        passive: false,
      });
      container.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
    }

    return () => {
      if (container) {
        container.removeEventListener("touchstart", handleTouchStart);
        container.removeEventListener("touchmove", handleTouchMove);
      }
    };
  }, [scale]);

  useEffect(() => {
    if (!showdrawingbox) return;

    const container = canvasContainerRef.current;
    if (!container || fabricRef.current) return;

    // Remove any existing canvas
    container.innerHTML = "";

    const canvasElement = document.createElement("canvas");
    canvasElement.width = 300;
    canvasElement.height = 200;
    canvasElement.className = "pointer-events-auto absolute inset-0";
    container.append(canvasElement);

    const fabricCanvas = new Canvas(canvasElement, {
      selection: false,
    });

    const brush = new PencilBrush(fabricCanvas);
    brush.width = 2;
    brush.color = selectedColor;

    fabricCanvas.isDrawingMode = true;
    fabricCanvas.freeDrawingBrush = brush;

    fabricRef.current = fabricCanvas;

    return () => {
      fabricCanvas.dispose();
      fabricRef.current = null;
      container.innerHTML = "";
    };
  }, [showdrawingbox]);

  useEffect(() => {
    const canvaElement = canvaContainerRef.current;
    const dragHandle = dragHandleRef.current;
    if (!canvaElement || !dragHandle) return;

    let isDragging = false;
    let offSetX = 0;
    let offSetY = 0;

    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      canvaElement.style.left = `${e.clientX - offSetX}px`;
      canvaElement.style.top = `${e.clientY - offSetY}px`;
    };

    const onMouseUp = () => {
      isDragging = false;
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    const onMouseDown = (e: MouseEvent) => {
      if (isDrawing) return;
      isDragging = true;
      offSetX = e.clientX - canvaElement.offsetLeft;
      offSetY = e.clientY - canvaElement.offsetTop;
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    };

    dragHandle.addEventListener("mousedown", onMouseDown);

    return () => {
      dragHandle.removeEventListener("mousedown", onMouseDown);
    };
  }, [isDrawing]);

  useEffect(() => {
    const container = pdfOverlayContainerRef.current;
    if (!container || !pdfSize.width || !pdfSize.height) return;

    // Clear any existing content
    container.innerHTML = "";

    // Create canvas element
    const canvasElement = document.createElement("canvas");
    canvasElement.width = pdfSize.width;
    canvasElement.height = pdfSize.height;
    canvasElement.className = "w-full h-full";

    //  Set a unique id for the canvas
    canvasElement.id = "pdf-overlay-canvas";

    container.append(canvasElement);

    // Initialize Fabric.js with the created canvas element
    const fabricCanvas = new Canvas(canvasElement, {
      selection: true,
      preserveObjectStacking: true,
      width: pdfSize.width,
      height: pdfSize.height,
    });

    pdfOverlayFabricRef.current = fabricCanvas;

    return () => {
      fabricCanvas.dispose();
      pdfOverlayFabricRef.current = null;
      container.innerHTML = "";
    };
  }, [pdfSize]);

  const handleExportDrawingToPdf = async () => {
    try {
      // Get the drawing data URL from your drawing canvas

      const dataUrl = fabricRef.current?.toDataURL({
        format: "png",
        multiplier: 2,
      });

      if (!dataUrl || !pdfOverlayFabricRef.current) {
        console.error("Missing dataUrl or pdfOverlayFabricRef");
        return;
      }

      // Create and add the image to overlay (existing functionality)
      const imgElement = new Image();
      imgElement.crossOrigin = "anonymous";

      imgElement.addEventListener("load", async () => {
        try {
          // Calculate scale to fit the drawing properly
          const scaleX = pdfSize.width / imgElement.width;
          const scaleY = pdfSize.height / imgElement.height;
          const scale = Math.min(scaleX, scaleY);

          const img = new FabricImage(imgElement, {
            left: 50,
            top: 50,
            selectable: true,
            hasControls: true,
            hasBorders: true,
            cornerColor: "blue",
            borderColor: "blue",
            scaleX: scale,
            scaleY: scale,
          });

          // Add to overlay canvas
          pdfOverlayFabricRef.current?.add(img);
          pdfOverlayFabricRef.current?.setActiveObject(img);
          pdfOverlayFabricRef.current?.renderAll();

          console.log("Image added successfully to overlay");
        } catch (error) {
          console.error("Error adding image to overlay:", error);
        }
      });

      imgElement.src = dataUrl;
    } catch (error) {
      console.error("Error in export process:", error);
    }
  };

  const drawStampOnCanvas = (imageUrl: string) => {
    try {
      // Add cache buster to avoid CORS issues with cached images that might lack CORS headers
      const busterUrl = imageUrl.startsWith("http")
        ? `${imageUrl}${imageUrl.includes("?") ? "&" : "?"}t=${Date.now()}`
        : imageUrl;

      const imgElement = new Image();
      imgElement.crossOrigin = "anonymous";

      imgElement.addEventListener("load", () => {
        if (!pdfOverlayFabricRef.current) {
          console.log("no pdf overlay ref");
          return;
        }

        const targetWidth = 150;
        const scale = targetWidth / imgElement.width;

        const img = new FabricImage(imgElement, {
          left: 100,
          top: 100,
          selectable: true,
          hasControls: true,
          hasBorders: true,
          cornerColor: "blue",
          borderColor: "blue",
          scaleX: scale,
          scaleY: scale,
        });

        pdfOverlayFabricRef.current.add(img);
        pdfOverlayFabricRef.current.setActiveObject(img);
        pdfOverlayFabricRef.current.renderAll();

        console.log("Stamp added to canvas");
      });
      imgElement.src = busterUrl;
    } catch (error) {
      console.error("Error adding stamp to canvas:", error);
    }
  };

  const handleSaveOverlayAsSingleImage = async (originalPdfBytes: any) => {
    try {
      if (!pdfOverlayFabricRef.current) {
        console.error("No overlay canvas found");
        return;
      }

      // Export the entire overlay canvas as image
      const overlayDataUrl = pdfOverlayFabricRef.current.toDataURL({
        format: "png",
        multiplier: 1, // Use 1 to maintain exact pixel dimensions
        left: 0,
        top: 0,
        width: pdfSize.width,
        height: pdfSize.height,
      });

      // Load the existing PDF
      const existingPdfBytes = await fetch(originalPdfBytes).then((res) =>
        res.arrayBuffer(),
      );
      const pdfDocument = await PDFDocument.load(existingPdfBytes);
      const pages = pdfDocument.getPages();
      const page = pages[pageNumber - 1];

      // Convert overlay image and embed
      const imageBytes = await fetch(overlayDataUrl).then((res) =>
        res.arrayBuffer(),
      );
      const overlayImage = await pdfDocument.embedPng(imageBytes);

      // Get PDF page dimensions
      const { width: pdfPageWidth, height: pdfPageHeight } = page.getSize();

      // Draw the overlay image to fill the entire page
      page.drawImage(overlayImage, {
        x: 0,
        y: 0,
        width: pdfPageWidth,
        height: pdfPageHeight,
      });

      // Save and download
      const pdfBytes = await pdfDocument.save();
      const blob = new Blob([pdfBytes as any], { type: "application/pdf" });
      const file = new File([blob], "updated-doc.pdf", {
        type: "application/pdf",
      });
      const url = URL.createObjectURL(blob);

      setDownloadUrl(url);

      await handleUpdateDoc(file, state);

      console.log("PDF saved with overlay image");
    } catch (error) {
      console.error("Error saving overlay to PDF:", error);
      toast.error("Failed to save PDF with overlay.");
    }
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = `${state?.title}.pdf`;
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(downloadUrl);
  };
  const handleDeleteCanva = () => {
    const activeObject = pdfOverlayFabricRef.current?.getActiveObject();
    if (activeObject) {
      pdfOverlayFabricRef.current?.remove(activeObject);
      pdfOverlayFabricRef.current?.discardActiveObject();
      pdfOverlayFabricRef.current?.renderAll();
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Delete" || e.key === "Escape") {
        handleDeleteCanva();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleClearCanvas = () => {
    const fabricCanvas = fabricRef.current;
    if (!fabricCanvas) return;

    fabricCanvas.clear();
  };
  const selectedRole =
    role === "REGISTRAR 1" ? 1 : role === "REGISTRAR 2" ? 2 : null;
  const handleUpdateDoc = async (file: File, item: any) => {
    setPreloader(true);
    try {
      const formData = new FormData();

      const fileBlob = new Blob([file], { type: "application/pdf" });
      formData.append("file_path", fileBlob, "file.pdf");

      const res: any = await Api.post(
        `registra${selectedRole}/case-documents/${
          item?.id || item?.case_document_id
        }/update`,
        formData,
      );
      console.log(res, "eeee");

      if (res) {
        toast.success(res?.data?.message);
        setDownloadPdf(true);
        setFileUrl(res?.data?.data?.file_path);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message, {
        duration: 3000,
      });
    } finally {
      setPreloader(false);
    }
  };

  const handleAddText = () => {
    // adds text to the pdf
    const canvas = pdfOverlayFabricRef.current;
    if (!canvas) return;
    const textBox = new Textbox("Enter text here", {
      left: 100,
      top: 100,
      fontSize: 20,
      fill: "#000",

      fontFamily: "Arial",
      editable: true,
      hasBorders: true,
      hasControls: true,
      borderColor: "blue",
      cornerColor: "blue",
    });

    canvas.add(textBox);
    canvas.setActiveObject(textBox);
    canvas.renderAll();
  };

  const saveStateForUndo = () => {
    const canvas = pdfOverlayFabricRef.current;
    if (!canvas) return;

    const activeCanvaObject = canvas.getActiveObject();

    if (canvas && activeCanvaObject && activeCanvaObject.type === "textbox") {
      const clonedState = activeCanvaObject.toObject();
      undoStack.push(clonedState);
      redoStack.length = 0; // we clear redo stack on new edit
    }
  };
  const updateTextStyle = (styleProps: object) => {
    const canvas = pdfOverlayFabricRef.current;
    if (!canvas) return;
    const activeObject = canvas.getActiveObject();
    if (activeObject && activeObject.type === "textbox") {
      saveStateForUndo();
      activeObject.set({ ...styleProps });
      canvas.renderAll();
    }
  };

  const ToggleTextStyle = (style: string) => {
    const canvas = pdfOverlayFabricRef.current;
    if (!canvas) return;
    const activeObject = canvas.getActiveObject();
    if (activeObject && activeObject.type === "textbox") {
      // @ts-expect-error
      const currentValue = activeObject[style];

      let newValue;
      switch (style) {
        case "fontWeight": {
          newValue = currentValue === "bold" ? "normal" : "bold";
          break;
        }
        case "fontStyle": {
          newValue = currentValue === "italic" ? "normal" : "italic";
          break;
        }
        case "underline": {
          newValue = !currentValue;
          break;
        }
        default: {
          return;
        }
      }
      saveStateForUndo();
      activeObject.set(style, newValue);
      canvas.renderAll();
    }
  };

  const handleUndo = () => {
    const canvas = pdfOverlayFabricRef.current;
    const activeObject = canvas?.getActiveObject();

    if (canvas && activeObject && undoStack.length > 0) {
      const lastState = undoStack.pop();
      const currentState = activeObject.toObject();
      redoStack.push(currentState);
      // @ts-ignore
      activeObject.set(lastState);
      canvas.renderAll();
    }
  };
  const handleRedo = () => {
    const canvas = pdfOverlayFabricRef.current;
    const activeObject = canvas?.getActiveObject();

    if (canvas && activeObject && redoStack.length > 0) {
      const nextState = redoStack.pop();
      const currentState = activeObject.toObject();
      undoStack.push(currentState);
      // @ts-ignore
      activeObject.set(nextState);
      canvas.renderAll();
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key.toLowerCase() === "z" && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if (
        (e.ctrlKey && e.key.toLowerCase() === "y") ||
        (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === "z")
      ) {
        e.preventDefault();
        handleRedo();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // console.log("selected - stamp", selectedStamp);

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar */}
      <div className="w-52 bg-[#272755] border-r">
        <ThumbnailSidebar
          numberPages={numberPages}
          pageNumber={pageNumber}
          pdfFile={fileUrl || ""}
          setPageNumber={setPageNumber}
        />
      </div>

      {/* Main Editor */}
      <div className="flex-1 flex flex-col">
        {/* Top Toolbar */}
        <div className="flex items-center p-5 justify-between w-full">
          <div className="back flex items-center gap-x-2">
            <button
              className="rounded-full bg-[#F2F2F7] p-2"
              onClick={() => window.history.back()}
              type="button"
            >
              <ArrowLeftIcon className="text-black w-3 " />
            </button>
            <span className="text-lg font-semibold text-black">
              {state.title}
            </span>
          </div>

          <div className="flex items-center justify-center gap-x-3">
            {/* <button
              onClick={handleUndo}
              className='px-6 py-2 font-medium  text-sm rounded-xl bg-gray-200 text-black'
              type='button'
            >
              undo
            </button>
            <button
              onClick={handleRedo}
              className='px-6 py-2 font-medium  text-sm rounded-xl bg-gray-200 text-black'
              type='button'
            >
              redo
            </button> */}
            <button
              className={`px-6 py-2 flex items-center justify-center font-medium ${
                preloader
              } text-sm rounded-xl bg-black text-white`}
              disabled={preloader}
              onClick={async () => handleSaveOverlayAsSingleImage(fileUrl)}
              type="button"
            >
              {preloader ? <LuLoader2 className="animate-spin" /> : "Save"}
            </button>
          </div>
        </div>
        <div className="px-5 pb-3 border-b shadow-lg bg-white  flex w-full items-center justify-between">
          <PdfToolbar
            drawImagetoPdf={drawStampOnCanvas}
            handleAddText={handleAddText}
            numberPages={numberPages}
            pageNumber={pageNumber}
            selectedStamp={selectedStamp}
            setSelectedStamp={setSelectedStamp}
            setShowdrawingbox={setShowdrawingbox}
            showdrawingbox={showdrawingbox}
          />

          <ZoomDropdown containerRef={viewerRef} setScale={setScale} />
        </div>
        {/* PDF View */}
        <TextToolbar
          downloadPdf={downloadPdf}
          handleDownload={handleDownload}
          selectedFont={selectedFont}
          selectedSize={selectedSize}
          setSelectedColor={setSelectedColor}
          setSelectedFont={setSelectedFont}
          setSelectedSize={setSelectedSize}
          ToggleTextStyle={ToggleTextStyle}
          updateTextStyle={updateTextStyle}
        />
        <div
          className="flex-1 overflow-auto  touch-none pt-[22rem] flex justify-center items-center bg-gray-50"
          ref={viewerRef}
        >
          <div
            className="transition-transform duration-300 ease-in-out mt-10 origin-top relative"
            ref={pdfPageRef}
            style={{ transform: `scale(${scale})` }}
          >
            <Document
              file={fileUrl}
              key={fileUrl}
              onLoadSuccess={handleDocumentLoad}
            >
              <Page
                pageNumber={pageNumber}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                scale={1}
                onRenderSuccess={handlePageOnloadSuccess}
              />
            </Document>

            {/* saved drawing here */}

            {/* display saved drawing here */}
            <div
              className="absolute top-0 left-0 z-30 w-full h-full pointer-events-auto"
              ref={pdfOverlayContainerRef}
              style={{
                transform: `scale(${scale})`,
                transformOrigin: "top left",
              }}
            />
          </div>
        </div>
        {showdrawingbox && (
          <div
            className="fixed top-24 left-24 z-50 bg-white shadow-lg border border-gray-300"
            ref={canvaContainerRef}
            style={{ width: 300, height: 200 }}
          >
            <div
              className="cursor-move flex justify-between items-center px-2 py-1 bg-gray-100 border-b border-gray-300"
              ref={dragHandleRef}
            >
              <span className="text-sm text-gray-600">Drawing</span>
              <div className="flex items-center gap-4">
                {startedDrawing && (
                  <>
                    {/* <button
                      title='undo'
                      onClick={handleUndo}
                      // onClick={handleExportDrawimgAsImage}
                      className='text-gray-500 hover:text-orange-500 text-sm font-bold'
                    >
                      <FaUndo />
                    </button>
                    <button
                      title='redo'
                      onClick={handleRedo}
                      className='text-gray-500 hover:text-green-500 text-sm font-bold'
                    >
                      <FaRedo />
                    </button> */}
                    <button
                      className="text-gray-500 hover:text-orange-500 text-sm font-bold"
                      onClick={handleClearCanvas}
                      title="erase"
                    >
                      <FaEraser />
                    </button>
                    <button
                      className="text-gray-500 hover:text-green-500 text-sm font-bold"
                      onClick={handleExportDrawingToPdf}
                      title="save"
                    >
                      <FaCheck />
                    </button>
                  </>
                )}
                <button
                  className="text-gray-500 hover:text-red-500 text-sm font-bold"
                  onClick={() => {
                    setShowdrawingbox(false);
                    setStartedDrawing(false);
                  }}
                  title="close"
                >
                  ✕
                </button>
              </div>
            </div>

            <div
              className="relative w-full h-[calc(100%-32px)]"
              onMouseDown={() => {
                setIsDrawing(true);
                setStartedDrawing(true);
              }}
              onMouseUp={() => setIsDrawing(false)}
            >
              {!isDrawing && (
                <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm pointer-events-none z-10">
                  Draw here
                </div>
              )}
              {/* Fabric canvas will go here */}

              <div className="absolute inset-0 z-20" ref={canvasContainerRef} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PdfEditor;
